import { createClient } from '@supabase/supabase-js'

// Export URL for use by network utilities (OfflineBanner, refreshManager)
export const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    },
    timeout: 20000,
    heartbeatIntervalMs: 30000,
    reconnectAfterMs: (tries) => Math.min(tries * 1000, 30000),
    logger: (level, message, details) => {
      if (level === 'error') {
        console.error('[Supabase Realtime]', message, details)
      } else if (import.meta.env.DEV) {
        console.log(`[Supabase Realtime ${level}]`, message, details)
      }
    }
  }
})

// Auth helper functions
export const authAPI = {
  // Sign up with email and password
  async signUp(email, password, fullName = null) {
    const signUpOptions = {
      email,
      password,
    }
    
    // Add user metadata if fullName is provided
    if (fullName) {
      signUpOptions.options = {
        data: {
          full_name: fullName
        }
      }
    }
    
    const { data, error } = await supabase.auth.signUp(signUpOptions)
    return { data, error }
  },

  // Sign in with email and password
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  },


  // Sign out (local scope - only current device/browser)
  async signOut() {
    const { error } = await supabase.auth.signOut({ scope: 'local' })
    return { error }
  },

  // Reset password
  async resetPassword(email) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    return { data, error }
  },

  // Update password
  async updatePassword(password) {
    const { data, error } = await supabase.auth.updateUser({
      password
    })
    return { data, error }
  },

  // Update user profile/metadata
  async updateUser(updates) {
    const { data, error } = await supabase.auth.updateUser(updates)
    return { data, error }
  },

  // Get current session
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession()
    return { session, error }
  },

  // Get current user
  getCurrentUser() {
    return supabase.auth.getUser()
  },

  // Listen to auth changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Board Columns API functions
export const boardColumnsAPI = {
  // Get all board columns
  async getAllColumns(userId) {
    const { data, error } = await supabase
      .from('board_columns')
      .select('*')
      .eq('user_id', userId)
      .order('display_order', { ascending: true })
    
    return { data, error }
  },

  // Create new board column
  async createColumn(userId, columnData) {
    // Remove any temporary ID before inserting to database
    const { id, ...dataToInsert } = columnData
    
    const { data, error } = await supabase
      .from('board_columns')
      .insert([{ ...dataToInsert, user_id: userId }])
      .select()
      .single()
    
    return { data, error }
  },

  // Update board column
  async updateColumn(columnId, updates) {
    const { data, error } = await supabase
      .from('board_columns')
      .update(updates)
      .eq('id', columnId)
      .select()
      .single()
    
    return { data, error }
  },

  // Delete board column
  async deleteColumn(columnId) {
    const { error } = await supabase
      .from('board_columns')
      .delete()
      .eq('id', columnId)
    
    return { error }
  },

  // Update multiple columns (for reordering and batch updates)
  async updateColumns(columns) {
    if (!columns || columns.length === 0) {
      return { data: [], error: null }
    }

    // Filter out any columns with temporary IDs - these should not be in batch updates
    const validColumns = columns.filter(column =>
      column.id && !column.id.toString().startsWith('temp-')
    )

    if (validColumns.length === 0) {
      return {
        data: null,
        error: {
          message: 'No valid columns to update. Temporary IDs cannot be used in batch updates.',
          code: 'TEMP_ID_ERROR'
        }
      }
    }

    const updates = validColumns.map((column, index) => {
      return {
        id: column.id,
        user_id: column.user_id,
        name: column.name,
        display_order: index,
        wip_limit: column.wip_limit ?? null,
        status_id: column.status_id,
        icon_name: column.icon_name ?? null,
        custom_color: column.custom_color ?? null,
        created_at: column.created_at,
        updated_at: new Date().toISOString()
      }
    })

    const { data, error } = await supabase
      .from('board_columns')
      .upsert(updates, { onConflict: 'id' })
      .select()

    return { data, error }
  }
}

// Column Status Mappings API functions
export const columnStatusMappingsAPI = {
  // Get all column status mappings
  async getAllMappings(userId) {
    const { data, error } = await supabase
      .from('column_status_mappings')
      .select(`
        *,
        board_columns(*),
        task_statuses(*)
      `)
      .eq('user_id', userId)
    
    return { data, error }
  },

  // Create new mapping
  async createMapping(userId, mappingData) {
    // Remove any temporary ID before inserting to database
    const { id, ...dataToInsert } = mappingData
    
    // Validate that foreign key IDs are not temporary
    if (dataToInsert.column_id && dataToInsert.column_id.toString().startsWith('temp-')) {
      return { 
        data: null, 
        error: { 
          message: 'Cannot create mapping with temporary column ID. Column must be created first.',
          code: 'TEMP_ID_ERROR'
        }
      }
    }
    
    if (dataToInsert.status_id && dataToInsert.status_id.toString().startsWith('temp-')) {
      return { 
        data: null, 
        error: { 
          message: 'Cannot create mapping with temporary status ID. Status must be created first.',
          code: 'TEMP_ID_ERROR'
        }
      }
    }
    
    const { data, error } = await supabase
      .from('column_status_mappings')
      .insert([{ ...dataToInsert, user_id: userId }])
      .select(`
        *,
        board_columns(*),
        task_statuses(*)
      `)
      .single()
    
    return { data, error }
  },

  // Delete mapping
  async deleteMapping(mappingId) {
    const { error } = await supabase
      .from('column_status_mappings')
      .delete()
      .eq('id', mappingId)
    
    return { error }
  },

  // Initialize board setup for new users
  async initializeBoardSetup(userId) {
    const { error } = await supabase.rpc('create_default_board_setup', {
      user_uuid: userId
    })
    
    return { error }
  }
}

// User Settings API functions
export const userSettingsAPI = {
  // Get user settings
  async getUserSettings(userId) {
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .single()
    return { data, error }
  },

  // Upsert user settings using the database function
  async upsertUserSettings(userId, settings = {}, currentSettings = null) {
    try {
      // Use currentSettings from Redux state instead of fetching from database
      // This follows Redux-first pattern and eliminates unnecessary database GET
      const existing = currentSettings || {
        timezone: 'UTC',
        dateFormat: 'MM/dd/yyyy',
        timeFormat: '12h',
        themePreference: 'system',
        showDailyFocus: true,
        showInbox: true,
        showCritical: true,
        showOverdue: true,
        showFlagged: true,
        showDueSoon: true,
        showStuck: true,
        showQuickWin: true,
        realtime_enabled: false,
        attentionThresholds: {
          DUE_SOON_DAYS: 3,
          DUE_SOON_WARNING_DAYS: 2,
          STUCK_HOURS: 72,
          AGING_DAYS: 7
        },
        savedKanbanFilters: [],
        excludeWeekendsFromMetrics: false,
        selectedHolidayCalendars: [],
        customExcludedDates: [],
        uiScale: 'roomy'
      }

      // Merge incoming settings with existing settings from Redux
      const mergedSettings = {
        timezone: settings.timezone !== undefined ? settings.timezone : existing.timezone,
        dateFormat: settings.dateFormat !== undefined ? settings.dateFormat : existing.dateFormat,
        timeFormat: settings.timeFormat !== undefined ? settings.timeFormat : existing.timeFormat,
        themePreference: settings.themePreference !== undefined ? settings.themePreference : existing.themePreference,
        showDailyFocus: settings.showDailyFocus !== undefined ? settings.showDailyFocus : existing.showDailyFocus,
        showInbox: settings.showInbox !== undefined ? settings.showInbox : existing.showInbox,
        showCritical: settings.showCritical !== undefined ? settings.showCritical : existing.showCritical,
        showOverdue: settings.showOverdue !== undefined ? settings.showOverdue : existing.showOverdue,
        showFlagged: settings.showFlagged !== undefined ? settings.showFlagged : existing.showFlagged,
        showDueSoon: settings.showDueSoon !== undefined ? settings.showDueSoon : existing.showDueSoon,
        showStuck: settings.showStuck !== undefined ? settings.showStuck : existing.showStuck,
        showQuickWin: settings.showQuickWin !== undefined ? settings.showQuickWin : existing.showQuickWin,
        realtime_enabled: settings.realtime_enabled !== undefined ? settings.realtime_enabled : existing.realtime_enabled,
        attentionThresholds: settings.attentionThresholds !== undefined ? settings.attentionThresholds : existing.attentionThresholds,
        savedKanbanFilters: settings.savedKanbanFilters !== undefined ? settings.savedKanbanFilters : existing.savedKanbanFilters,
        excludeWeekendsFromMetrics: settings.excludeWeekendsFromMetrics !== undefined ? settings.excludeWeekendsFromMetrics : existing.excludeWeekendsFromMetrics,
        selectedHolidayCalendars: settings.selectedHolidayCalendars !== undefined ? settings.selectedHolidayCalendars : (existing.selectedHolidayCalendars || []),
        customExcludedDates: settings.customExcludedDates !== undefined ? settings.customExcludedDates : (existing.customExcludedDates || []),
        uiScale: settings.uiScale !== undefined ? settings.uiScale : (existing.uiScale || 'roomy')
      }

      // Always pass ALL parameters to avoid function overloading conflicts
      const { data: result, error: rpcError } = await supabase.rpc('upsert_user_settings', {
        user_uuid: userId,
        user_timezone: mergedSettings.timezone,
        user_date_format: mergedSettings.dateFormat,
        user_time_format: mergedSettings.timeFormat,
        theme_preference: mergedSettings.themePreference,
        show_daily_focus: mergedSettings.showDailyFocus,
        show_inbox: mergedSettings.showInbox,
        show_critical: mergedSettings.showCritical,
        show_overdue: mergedSettings.showOverdue,
        show_flagged: mergedSettings.showFlagged,
        show_due_soon: mergedSettings.showDueSoon,
        show_stuck: mergedSettings.showStuck,
        show_quick_win: mergedSettings.showQuickWin,
        realtime_enabled: mergedSettings.realtime_enabled,
        attention_thresholds: mergedSettings.attentionThresholds,
        saved_kanban_filters: mergedSettings.savedKanbanFilters,
        exclude_weekends_from_metrics: mergedSettings.excludeWeekendsFromMetrics,
        selected_holiday_calendars: mergedSettings.selectedHolidayCalendars,
        custom_excluded_dates: mergedSettings.customExcludedDates,
        ui_scale: mergedSettings.uiScale
      })

      return { data: result, error: rpcError }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Initialize user settings with detected timezone and default attention sidebar settings
  async initializeUserSettings(userId, detectedTimezone = 'UTC', defaultSettings = {}) {
    const { data, error } = await supabase.rpc('initialize_user_settings', {
      user_uuid: userId,
      detected_timezone: detectedTimezone,
      theme_preference: defaultSettings.themePreference ?? 'system',
      show_daily_focus: defaultSettings.showDailyFocus ?? true,
      show_inbox: defaultSettings.showInbox ?? true,
      show_critical: defaultSettings.showCritical ?? true,
      show_overdue: defaultSettings.showOverdue ?? true,
      show_flagged: defaultSettings.showFlagged ?? true,
      show_due_soon: defaultSettings.showDueSoon ?? true,
      show_stuck: defaultSettings.showStuck ?? true,
      show_quick_win: defaultSettings.showQuickWin ?? true,
      saved_kanban_filters: defaultSettings.savedKanbanFilters ?? [],
      exclude_weekends_from_metrics: defaultSettings.excludeWeekendsFromMetrics ?? false,
      selected_holiday_calendars: defaultSettings.selectedHolidayCalendars ?? [],
      custom_excluded_dates: defaultSettings.customExcludedDates ?? [],
      ui_scale: defaultSettings.uiScale ?? 'roomy'
    })
    return { data, error }
  },

  // Mark onboarding as completed
  async completeOnboarding(userId) {
    const { data, error } = await supabase.rpc('complete_user_onboarding', {
      user_uuid: userId
    })
    return { data, error }
  }

  // NOTE: User timezone is loaded via bootstrap and available in Redux state.
  // Pass timezone from Redux (state.userSettings.settings.timezone) to functions
  // that need it instead of making separate API calls.
}

// Performance Analytics API functions
export const performanceAPI = {
  // Get daily velocity data
  async getDailyVelocity(userId, daysBack = 14, userSettings = null) {
    // Include weekends by default, unless user has excluded them
    // Backward compatible: if userSettings is null/undefined, defaults to true
    const includeWeekends = userSettings?.excludeWeekendsFromMetrics ? false : true

    // Calculate custom excluded dates (holidays + custom exclusions)
    const selectedHolidayCalendars = userSettings?.selectedHolidayCalendars || []
    const customExcludedDates = userSettings?.customExcludedDates || []

    let excludedDatesArray = []
    if (selectedHolidayCalendars.length > 0 || customExcludedDates.length > 0) {
      // Dynamically import holiday calculations
      const { getAllExcludedDates } = await import('./holidayCalculations')

      // Calculate excluded dates for relevant year range
      const currentYear = new Date().getFullYear()
      const excludedDatesSet = getAllExcludedDates(
        selectedHolidayCalendars,
        customExcludedDates,
        currentYear - 1, // Include previous year for year-end transitions
        currentYear + 1  // Include next year for year-start transitions
      )

      // Convert Set to Array for PostgreSQL
      excludedDatesArray = Array.from(excludedDatesSet)
    }

    const { data, error } = await supabase.rpc('get_daily_velocity', {
      user_uuid: userId,
      days_back: daysBack,
      include_weekends: includeWeekends,
      custom_excluded_dates: excludedDatesArray
    })
    return { data, error }
  },

  // Get velocity summary statistics
  async getVelocitySummary(userId, daysBack = 14) {
    const { data, error } = await supabase.rpc('get_velocity_summary', {
      user_uuid: userId,
      days_back: daysBack
    })
    return { data, error }
  },

  // Get daily burndown data
  async getDailyBurndown(userId, daysBack = 14) {
    const { data, error } = await supabase.rpc('get_daily_burndown', {
      user_uuid: userId,
      days_back: daysBack,
      include_effort: false,
      filter_project_id: null,
      filter_goal_id: null
    })
    return { data, error }
  },

  // Get cycle time analysis using custom query with timezone awareness
  // Supports business days calculation when excludeWeekendsFromMetrics is enabled
  async getCycleTimeAnalysis(userId, daysBack = 14, userTimezone = null, userSettings = null) {
    try {
      // Import date utilities for timezone-aware date range and business days calculation
      const { getDateRangeInUserTimezone, calculateBusinessDays } = await import('./dateUtils')
      const dateRange = getDateRangeInUserTimezone(daysBack, userTimezone)

      const { data, error } = await supabase
        .from('task_events')
        .select(`
          task_id,
          event_type,
          created_at,
          tasks!inner(
            id,
            title,
            priority,
            effort_level,
            created_at
          )
        `)
        .eq('user_id', userId)
        .in('event_type', ['created', 'completed'])
        .gte('created_at', dateRange.startDate)
        .lte('created_at', dateRange.endDate)
        .order('task_id')
        .order('created_at')

      if (error) return { data: null, error }

      // Process exclusion settings
      const excludeWeekends = userSettings?.excludeWeekendsFromMetrics || false
      const selectedHolidayCalendars = userSettings?.selectedHolidayCalendars || []
      const customExcludedDates = userSettings?.customExcludedDates || []

      // Calculate excluded dates set if any exclusions are configured
      let excludedDatesSet = new Set()
      if (excludeWeekends || selectedHolidayCalendars.length > 0 || customExcludedDates.length > 0) {
        try {
          const { getAllExcludedDates } = await import('./holidayCalculations')
          // Get current year for holiday calculation range
          const currentYear = new Date().getFullYear()
          excludedDatesSet = getAllExcludedDates(
            selectedHolidayCalendars,
            customExcludedDates,
            currentYear - 1,  // Include previous year for tasks that span year boundary
            currentYear + 1   // Include next year for future tasks
          )
        } catch (importError) {
          console.warn('Could not load holiday calculations:', importError)
        }
      }

      // Process data to calculate cycle times
      const taskEvents = {}
      data.forEach(event => {
        if (!taskEvents[event.task_id]) {
          taskEvents[event.task_id] = {
            task: event.tasks,
            created: null,
            completed: null
          }
        }
        if (event.event_type === 'created') {
          taskEvents[event.task_id].created = event.created_at
        } else if (event.event_type === 'completed') {
          taskEvents[event.task_id].completed = event.created_at
        }
      })

      const cycleTimeData = Object.values(taskEvents)
        .filter(task => task.created && task.completed)
        .map(task => {
          const cycleTimeHours = (new Date(task.completed) - new Date(task.created)) / (1000 * 60 * 60)
          const calendarDays = cycleTimeHours / 24

          // Calculate business days when exclusions are enabled
          let businessDays = calendarDays
          if (excludeWeekends || excludedDatesSet.size > 0) {
            businessDays = calculateBusinessDays(
              task.created,
              task.completed,
              excludedDatesSet,
              excludeWeekends
            )
          }

          return {
            task_id: task.task.id,
            title: task.task.title,
            priority: task.task.priority,
            effort_level: task.task.effort_level,
            cycle_time_hours: cycleTimeHours,
            // Use business days when exclusions enabled, calendar days otherwise
            cycle_time_days: excludeWeekends ? businessDays : calendarDays,
            // Always include calendar days for reference
            cycle_time_calendar_days: calendarDays,
            created_at: task.created,
            completed_at: task.completed
          }
        })

      return { data: cycleTimeData, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Get productivity patterns using custom query with timezone awareness
  async getProductivityPatterns(userId, daysBack = 14, userTimezone = null) {
    try {
      // Import date utilities for timezone-aware date range and conversions
      const { getDateRangeInUserTimezone, getHourInUserTimezone, getDayOfWeekInUserTimezone } = await import('./dateUtils')
      const dateRange = getDateRangeInUserTimezone(daysBack, userTimezone)

      const { data, error } = await supabase
        .from('task_events')
        .select('created_at')
        .eq('user_id', userId)
        .eq('event_type', 'completed')
        .gte('created_at', dateRange.startDate)
        .lte('created_at', dateRange.endDate)
        .order('created_at')

      if (error) return { data: null, error }

      // Process data to extract productivity patterns using user timezone
      const hourlyPattern = {}
      const dailyPattern = {}
      
      data.forEach(event => {
        // Convert UTC timestamp to user timezone for hour/day extraction
        const hour = getHourInUserTimezone(event.created_at, userTimezone)
        const dayOfWeek = getDayOfWeekInUserTimezone(event.created_at, userTimezone)

        hourlyPattern[hour] = (hourlyPattern[hour] || 0) + 1
        dailyPattern[dayOfWeek] = (dailyPattern[dayOfWeek] || 0) + 1
      })

      const productivityPatterns = {
        hourlyPattern: Object.entries(hourlyPattern).map(([hour, count]) => ({
          hour: parseInt(hour),
          completions: count
        })).sort((a, b) => a.hour - b.hour),
        
        dailyPattern: Object.entries(dailyPattern).map(([day, count]) => ({
          day: parseInt(day),
          dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][parseInt(day)],
          completions: count
        })).sort((a, b) => a.day - b.day),

        peakHour: Object.entries(hourlyPattern).reduce((max, [hour, count]) => 
          count > (hourlyPattern[max] || 0) ? hour : max, '9'),
        
        peakDay: Object.entries(dailyPattern).reduce((max, [day, count]) => 
          count > (dailyPattern[max] || 0) ? day : max, '1')
      }

      return { data: productivityPatterns, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }
}

// Tasks API functions
export const tasksAPI = {
  // Get all tasks with enhanced data including labels, ordered by position within each status
  async getAllTasks(userId) {
    const { data, error } = await supabase
      .rpc('get_tasks_with_labels', { p_user_id: userId })

    if (error) {
      return { data: null, error }
    }

    if (data) {
      // Extract task_data from each row and flatten nested data for easier access
      return {
        data: data.map(row => {
          const task = row.task_data
          return {
            ...task,
            annual_goal_title: task.weekly_goals?.quarterly_goals?.annual_goals?.title || null,
            quarterly_milestone_title: task.weekly_goals?.quarterly_goals?.title || null,
            status_name: task.task_statuses?.name || null,
            status_color: task.task_statuses?.color || null
          }
        }),
        error: null
      }
    }

    return { data, error }
  },

  // Create new task with proper order_position
  async createTask(userId, taskData, labelIds = []) {
    try {
      // Use atomic database function for task creation with labels
      // This eliminates the order_position query and handles labels in one transaction
      const { data, error } = await supabase.rpc('create_task_with_labels', {
        p_user_id: userId,
        p_title: taskData.title,
        p_description: taskData.description || null,
        p_status_id: taskData.status_id || null,
        p_project_id: taskData.project_id || null,
        p_weekly_goal_id: taskData.weekly_goal_id || null,
        p_priority: taskData.priority || 'medium',
        p_effort_level: taskData.effort_level || null,
        p_due_date: taskData.due_date || null,
        p_flagged: taskData.flagged || false,
        p_plan: taskData.plan || null,
        p_label_ids: labelIds.length > 0 ? labelIds : null
      })

      if (error) return { data: null, error }

      // Transform JSONB labels array to objects format expected by Redux
      const transformedData = {
        ...data,
        labels: Array.isArray(data.labels) ? data.labels : []
      }

      return { data: transformedData, error: null }
    } catch (error) {
      console.error('Error in createTask:', error)
      return { data: null, error }
    }
  },

  // Update task
  async updateTask(taskId, updates, labelIds = null, userId = null) {
    try {
      // If labelIds provided and userId available, use atomic function for label sync
      if (labelIds !== null && userId) {
        const { data, error } = await supabase.rpc('update_task_with_labels', {
          p_task_id: taskId,
          p_user_id: userId,
          p_updates: updates,
          p_label_ids: labelIds
        })

        if (error) return { data: null, error }

        // Transform JSONB labels array to objects format expected by Redux
        const transformedData = {
          ...data,
          labels: Array.isArray(data.labels) ? data.labels : []
        }

        return { data: transformedData, error: null }
      }

      // Fallback to direct update if no label sync needed
      // Return task with ID references only (no joined objects)
      // Relationships looked up from Redux state via project_id, weekly_goal_id, status_id
      const { data, error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', taskId)
        .select('*')
        .single()

      // Return raw task data with ID references only
      return { data, error }
    } catch (error) {
      console.error('Error in updateTask:', error)
      return { data: null, error }
    }
  },

  // Delete task
  async deleteTask(taskId) {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId)
    
    return { error }
  },

  // Archive task
  async archiveTask(taskId) {
    const { data, error } = await supabase
      .from('tasks')
      .update({ 
        archived: true, 
        archived_at: new Date().toISOString() 
      })
      .eq('id', taskId)
      .select(`
        *,
        task_statuses(
          id,
          name,
          color,
          is_completion_status
        ),
        projects (
          id,
          name
        ),
        weekly_goals (
          id,
          title,
          week_start_date,
          quarterly_goals (
            id,
            title,
            annual_goals (
              id,
              title
            )
          )
        )
      `)
      .single()

    if (data) {
      return {
        data: {
          ...data,
          annual_goal_title: data.weekly_goals?.quarterly_goals?.annual_goals?.title || null,
          quarterly_milestone_title: data.weekly_goals?.quarterly_goals?.title || null,
          status_name: data.task_statuses?.name || null,
          status_color: data.task_statuses?.color || null
        },
        error
      }
    }

    return { data, error }
  },

  // Unarchive task
  async unarchiveTask(taskId) {
    const { data, error } = await supabase
      .from('tasks')
      .update({ 
        archived: false, 
        archived_at: null 
      })
      .eq('id', taskId)
      .select(`
        *,
        task_statuses(
          id,
          name,
          color,
          is_completion_status
        ),
        projects (
          id,
          name
        ),
        weekly_goals (
          id,
          title,
          week_start_date,
          quarterly_goals (
            id,
            title,
            annual_goals (
              id,
              title
            )
          )
        )
      `)
      .single()

    if (data) {
      return {
        data: {
          ...data,
          annual_goal_title: data.weekly_goals?.quarterly_goals?.annual_goals?.title || null,
          quarterly_milestone_title: data.weekly_goals?.quarterly_goals?.title || null,
          status_name: data.task_statuses?.name || null,
          status_color: data.task_statuses?.color || null
        },
        error
      }
    }

    return { data, error }
  },

  // Bulk archive tasks
  async bulkArchiveTasks(taskIds) {
    const { data, error } = await supabase
      .from('tasks')
      .update({ 
        archived: true, 
        archived_at: new Date().toISOString() 
      })
      .in('id', taskIds)
      .select(`
        *,
        task_statuses(
          id,
          name,
          color,
          is_completion_status
        ),
        projects (
          id,
          name
        ),
        weekly_goals (
          id,
          title,
          week_start_date,
          quarterly_goals (
            id,
            title,
            annual_goals (
              id,
              title
            )
          )
        )
      `)

    if (data) {
      return {
        data: data.map(task => ({
          ...task,
          annual_goal_title: task.weekly_goals?.quarterly_goals?.annual_goals?.title || null,
          quarterly_milestone_title: task.weekly_goals?.quarterly_goals?.title || null,
          status_name: task.task_statuses?.name || null,
          status_color: task.task_statuses?.color || null
        })),
        error
      }
    }

    return { data, error }
  },

  // Reorder tasks within a column - efficient single API call using Redux state data
  async reorderTasksInColumn(userId, statusId, tasksWithNewPositions) {
    try {
      // Validate inputs
      if (!userId || !statusId || !Array.isArray(tasksWithNewPositions) || tasksWithNewPositions.length === 0) {
        throw new Error('Invalid parameters for task reordering')
      }

      // Create batch update payload using existing task data from Redux state
      const now = new Date().toISOString()
      const batchUpdates = tasksWithNewPositions.map((task) => {
        // Preserve all existing fields, only update order_position and updated_at
        return {
          id: task.id,
          user_id: userId,
          title: task.title,
          description: task.description,
          status_id: task.status_id,
          project_id: task.project_id,
          weekly_goal_id: task.weekly_goal_id,
          priority: task.priority,
          effort_level: task.effort_level,
          due_date: task.due_date,
          completed: task.completed,
          archived: task.archived,
          flagged: task.flagged,
          created_at: task.created_at,
          archived_at: task.archived_at,
          order_position: task.order_position, // Updated position from Redux
          updated_at: now // Update timestamp
        }
      })

      // Single batch upsert operation - no fetch needed!
      const { data: updatedTasks, error: updateError } = await supabase
        .from('tasks')
        .upsert(batchUpdates, { onConflict: 'id' })
        .select(`
          *,
          task_statuses(
            id,
            name,
            color,
            is_completion_status
          ),
          projects (
            id,
            name
          ),
          weekly_goals (
            id,
            title,
            week_start_date,
            quarterly_goals (
              id,
              title,
              annual_goals (
                id,
                title
              )
            )
          )
        `)

      if (updateError) {
        throw updateError
      }
      
      // Flatten nested data for easier access
      const formattedTasks = updatedTasks.map(task => ({
        ...task,
        annual_goal_title: task.weekly_goals?.quarterly_goals?.annual_goals?.title || null,
        quarterly_milestone_title: task.weekly_goals?.quarterly_goals?.title || null,
        status_name: task.task_statuses?.name || null,
        status_color: task.task_statuses?.color || null
      }))

      return { data: formattedTasks, error: null }
    } catch (error) {
      console.error('Task reordering failed:', error)
      return { 
        data: null, 
        error: {
          message: error.message || 'Failed to reorder tasks',
          details: error
        }
      }
    }
  }
}

// Projects API functions
export const projectsAPI = {
  // Get all projects
  async getAllProjects(userId) {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .order('display_order', { ascending: true })
    
    return { data, error }
  },

  // Create new project
  async createProject(userId, projectData) {
    const { data, error } = await supabase
      .from('projects')
      .insert([{ ...projectData, user_id: userId }])
      .select()
      .single()
    
    return { data, error }
  },

  // Update project
  async updateProject(projectId, updates) {
    const { data, error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', projectId)
      .select()
      .single()
    
    return { data, error }
  },

  // Delete project
  async deleteProject(projectId) {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId)
    
    return { error }
  },

  // Update project order - optimized batch operation
  async updateProjectOrder(projects) {
    // Create array of updates with id and new display_order
    const updates = projects.map((project, index) => ({
      id: project.id,
      display_order: index,
      // Include other existing fields to ensure they're preserved
      user_id: project.user_id,
      name: project.name,
      description: project.description,
      status: project.status,
      due_date: project.due_date,
      created_at: project.created_at,
      updated_at: new Date().toISOString()
    }))

    // Single batch operation using upsert
    const { data, error } = await supabase
      .from('projects')
      .upsert(updates, { onConflict: 'id' })
      .select()

    return { data, error }
  }
}

// Task Statuses API functions
export const taskStatusesAPI = {
  // Get all task statuses
  async getAllStatuses(userId) {
    const { data, error } = await supabase
      .from('task_statuses')
      .select('*')
      .eq('user_id', userId)
      .order('display_order', { ascending: true })
    
    return { data, error }
  },

  // Create new task status
  async createStatus(userId, statusData) {
    const { data, error } = await supabase
      .from('task_statuses')
      .insert([{ ...statusData, user_id: userId }])
      .select()
      .single()
    
    return { data, error }
  },

  // Update task status
  async updateStatus(statusId, updates) {
    const { data, error } = await supabase
      .from('task_statuses')
      .update(updates)
      .eq('id', statusId)
      .select()
      .single()
    
    return { data, error }
  },

  // Delete task status
  async deleteStatus(statusId) {
    const { error } = await supabase
      .from('task_statuses')
      .delete()
      .eq('id', statusId)
    
    return { error }
  }
}


// Task Attachments API functions
export const taskAttachmentsAPI = {
  // Upload attachment to storage and create database record
  async uploadAttachment(userId, taskId, file) {
    try {
      // Generate unique file path
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `${userId}/${taskId}/${fileName}`

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('task-attachments')
        .upload(filePath, file)

      if (uploadError) {
        throw uploadError
      }

      // Create database record
      const { data: dbData, error: dbError } = await supabase
        .from('task_attachments')
        .insert([{
          user_id: userId,
          task_id: taskId,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          storage_path: filePath
        }])
        .select()
        .single()

      if (dbError) {
        // Clean up uploaded file if database insert fails
        await supabase.storage
          .from('task-attachments')
          .remove([filePath])
        throw dbError
      }

      return { data: dbData, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Get all attachments for a task
  async getTaskAttachments(taskId) {
    const { data, error } = await supabase
      .from('task_attachments')
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: true })

    return { data, error }
  },

  // Delete attachment (removes from storage and database)
  async deleteAttachment(attachmentId) {
    try {
      // First get the attachment record to find the storage path
      const { data: attachment, error: fetchError } = await supabase
        .from('task_attachments')
        .select('storage_path')
        .eq('id', attachmentId)
        .single()

      if (fetchError) {
        throw fetchError
      }

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('task-attachments')
        .remove([attachment.storage_path])

      if (storageError) {
        console.warn('Failed to delete file from storage:', storageError)
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('task_attachments')
        .delete()
        .eq('id', attachmentId)

      if (dbError) {
        throw dbError
      }

      return { error: null }
    } catch (error) {
      return { error }
    }
  },

  // Generate signed URL for file download
  async getDownloadUrl(storagePath) {
    const { data, error } = await supabase.storage
      .from('task-attachments')
      .createSignedUrl(storagePath, 3600) // 1 hour expiry

    return { data, error }
  },

  // Download attachment by ID
  async downloadAttachment(attachmentId) {
    try {
      // Get attachment record
      const { data: attachment, error: fetchError } = await supabase
        .from('task_attachments')
        .select('*')
        .eq('id', attachmentId)
        .single()

      if (fetchError) {
        throw fetchError
      }

      // Generate signed URL
      const { data: urlData, error: urlError } = await this.getDownloadUrl(attachment.storage_path)

      if (urlError) {
        throw urlError
      }

      return { 
        data: { 
          ...attachment, 
          download_url: urlData.signedUrl 
        }, 
        error: null 
      }
    } catch (error) {
      return { data: null, error }
    }
  }
}

// Task Events API functions
export const taskEventsAPI = {
  // Get time in current status for a task
  async getTimeInCurrentStatus(taskId) {
    try {
      const { data: statusChangeEvents, error } = await supabase
        .from('task_events')
        .select('created_at, to_status_id')
        .eq('task_id', taskId)
        .eq('event_type', 'status_changed')
        .order('created_at', { ascending: false })
        .limit(1)

      if (error) {
        console.error('Error fetching status change events:', error)
        return { data: null, error }
      }

      let hoursInStatus = null
      const now = new Date()

      if (statusChangeEvents && statusChangeEvents.length > 0) {
        // Use the most recent status change
        const lastStatusChange = new Date(statusChangeEvents[0].created_at)
        hoursInStatus = (now - lastStatusChange) / (1000 * 60 * 60)
      }

      return { data: hoursInStatus, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }
}

// MFA API functions (JIT approach - no state storage)
export const mfaAPI = {
  // Check current Authentication Assurance Level
  async getAuthenticatorAssuranceLevel() {
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      if (error) return { currentLevel: 'aal1', nextLevel: null, error }
      
      return { 
        currentLevel: data.currentLevel || 'aal1',
        nextLevel: data.nextLevel || null,
        error: null 
      }
    } catch (error) {
      return { currentLevel: 'aal1', nextLevel: null, error }
    }
  },

  // Check if user has MFA enabled (returns boolean only)
  async checkUserMFAStatus() {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors()
      if (error) return { hasMFA: false, error }
      
      const hasVerifiedFactors = data.totp?.some(f => f.status === 'verified') || false
      return { hasMFA: hasVerifiedFactors, error: null }
    } catch (error) {
      return { hasMFA: false, error }
    }
  },

  // Check if user has verified MFA factors and return them
  async getVerifiedFactors() {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors()
      if (error) return { factors: [], error }
      
      const verifiedFactors = data.totp?.filter(f => f.status === 'verified') || []
      return { factors: verifiedFactors, error: null }
    } catch (error) {
      return { factors: [], error }
    }
  },

  // Challenge an existing factor for step-up authentication
  async challengeExistingFactor(factorId) {
    try {
      const { data, error } = await supabase.auth.mfa.challenge({ factorId })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Verify MFA code for existing factor (step-up authentication)
  async verifyExistingFactor(factorId, challengeId, code) {
    try {
      const verify = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code: code.trim()
      })
      
      return { data: verify.data, error: verify.error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Start TOTP enrollment process
  async enrollTOTP() {
    try {
      // Generate a unique friendly name to avoid conflicts
      const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ')
      const friendlyName = `Authenticator ${timestamp}`
      
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: friendlyName
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Create challenge for verification (Supabase best practice)
  async createChallenge(factorId) {
    try {
      const { data, error } = await supabase.auth.mfa.challenge({ factorId })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Verify MFA challenge (used for password reset flow and step-up auth)
  async verifyChallenge(factorId, challengeId, code) {
    try {
      const { data, error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code: code.trim()
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Complete TOTP enrollment with verification (Supabase compliant)
  async completeTOTPEnrollment(factorId, challengeId, code) {
    try {
      const verify = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code: code.trim()
      })
      
      return { data: verify.data, error: verify.error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Get user's current factors (for management UI)
  async getUserFactors() {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors()
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Remove specific factor
  async unenrollFactor(factorId) {
    try {
      const { data, error } = await supabase.auth.mfa.unenroll({ factorId })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Remove all MFA factors (complete disable)
  async disableAllMFA() {
    try {
      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors()
      if (listError) return { error: listError }
      
      // Remove all TOTP factors
      if (factors.totp && factors.totp.length > 0) {
        for (const factor of factors.totp) {
          const { error: unenrollError } = await supabase.auth.mfa.unenroll({ 
            factorId: factor.id 
          })
          if (unenrollError) return { error: unenrollError }
        }
      }
      
      return { error: null }
    } catch (error) {
      return { error }
    }
  }
}

// Goals API functions
export const goalsAPI = {
  // Annual Goals
  async getAllAnnualGoals(userId) {
    const { data, error } = await supabase
      .from('annual_goals')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    
    return { data, error }
  },

  async createAnnualGoal(userId, goalData) {
    const { data, error } = await supabase
      .from('annual_goals')
      .insert([{ ...goalData, user_id: userId }])
      .select()
      .single()
    
    return { data, error }
  },

  async updateAnnualGoal(goalId, updates) {
    const { data, error } = await supabase
      .from('annual_goals')
      .update(updates)
      .eq('id', goalId)
      .select()
      .single()
    
    return { data, error }
  },

  async deleteAnnualGoal(goalId) {
    const { error } = await supabase
      .from('annual_goals')
      .delete()
      .eq('id', goalId)
    
    return { error }
  },

  async getAvailableYears(userId) {
    const { data, error } = await supabase
      .from('annual_goals')
      .select('target_year')
      .eq('user_id', userId)

    if (error) return { data: null, error }

    // Extract unique years and sort them
    const years = [...new Set(data.map(goal => goal.target_year))]
      .filter(year => year !== null && year !== undefined)
      .sort((a, b) => b - a) // Sort in descending order (newest first)

    return { data: years, error: null }
  },

  // Quarterly Goals
  async getAllQuarterlyGoals(userId) {
    const { data, error } = await supabase
      .from('quarterly_goals')
      .select(`
        *,
        annual_goals (
          id,
          title,
          category
        )
      `)
      .eq('user_id', userId)
      .order('year', { ascending: false })
      .order('quarter', { ascending: false })
    
    return { data, error }
  },

  async createQuarterlyGoal(userId, goalData) {
    const { data, error } = await supabase
      .from('quarterly_goals')
      .insert([{ ...goalData, user_id: userId }])
      .select(`
        *,
        annual_goals (
          id,
          title,
          category
        )
      `)
      .single()
    
    return { data, error }
  },

  async updateQuarterlyGoal(goalId, updates) {
    const { data, error } = await supabase
      .from('quarterly_goals')
      .update(updates)
      .eq('id', goalId)
      .select(`
        *,
        annual_goals (
          id,
          title,
          category
        )
      `)
      .single()
    
    return { data, error }
  },

  async deleteQuarterlyGoal(goalId) {
    const { error } = await supabase
      .from('quarterly_goals')
      .delete()
      .eq('id', goalId)
    
    return { error }
  },

  // Weekly Goals
  async getAllWeeklyGoals(userId) {
    const { data, error } = await supabase
      .from('weekly_goals')
      .select(`
        *,
        quarterly_goals (
          id,
          title,
          quarter,
          year,
          annual_goals (
            id,
            title,
            category
          )
        )
      `)
      .eq('user_id', userId)
      .order('week_start_date', { ascending: false })
    
    return { data, error }
  },

  async createWeeklyGoal(userId, goalData) {
    const { data, error } = await supabase
      .from('weekly_goals')
      .insert([{ ...goalData, user_id: userId }])
      .select(`
        *,
        quarterly_goals (
          id,
          title,
          quarter,
          year,
          annual_goals (
            id,
            title,
            category
          )
        )
      `)
      .single()
    
    return { data, error }
  },

  async updateWeeklyGoal(goalId, updates) {
    const { data, error } = await supabase
      .from('weekly_goals')
      .update(updates)
      .eq('id', goalId)
      .select(`
        *,
        quarterly_goals (
          id,
          title,
          quarter,
          year,
          annual_goals (
            id,
            title,
            category
          )
        )
      `)
      .single()
    
    return { data, error }
  },

  async deleteWeeklyGoal(goalId) {
    const { error } = await supabase
      .from('weekly_goals')
      .delete()
      .eq('id', goalId)
    
    return { error }
  },

  // Goal scheduling operations
  async promoteGoal(goalId, goalType, schedulingData) {
    let table = ''
    let updateData = { status: 'scheduled', ...schedulingData }

    switch (goalType) {
      case 'annual':
        table = 'annual_goals'
        if (!schedulingData.target_year) {
          throw new Error('Target year is required for annual goals')
        }
        break
      case 'quarterly':
        table = 'quarterly_goals'
        if (!schedulingData.quarter || !schedulingData.year) {
          throw new Error('Quarter and year are required for quarterly goals')
        }
        break
      case 'weekly':
        table = 'weekly_goals'
        if (!schedulingData.week_start_date) {
          throw new Error('Week start date is required for weekly goals')
        }
        break
      default:
        throw new Error('Invalid goal type')
    }

    const { data, error } = await supabase
      .from(table)
      .update(updateData)
      .eq('id', goalId)
      .select('*')
      .single()

    return { data: { goalType, data }, error }
  },

  async cascadeUnscheduleGoal(goalId, goalType) {
    const { data, error } = await supabase.rpc('cascade_unschedule_goal', {
      p_goal_id: goalId,
      p_goal_type: goalType
    })

    if (error) {
      return { data: null, error }
    }

    return {
      data: {
        annualGoal: data.annual_goal,
        quarterlyGoals: data.quarterly_goals || [],
        weeklyGoals: data.weekly_goals || []
      },
      error: null
    }
  },

  async cascadeRescheduleQuarterlyGoal(goalId, newQuarter, newYear, otherUpdates = {}) {
    const { data, error } = await supabase.rpc('cascade_reschedule_quarterly_goal', {
      p_goal_id: goalId,
      p_new_quarter: newQuarter,
      p_new_year: newYear,
      p_other_updates: otherUpdates
    })

    if (error) {
      return { data: null, error }
    }

    return {
      data: {
        quarterlyGoal: data.quarterly_goal,
        weeklyGoals: data.weekly_goals || []
      },
      error: null
    }
  },

  async cascadeCompleteGoal(goalId, goalType, cascade = true) {
    const { data, error } = await supabase.rpc('cascade_complete_goal', {
      p_goal_id: goalId,
      p_goal_type: goalType,
      p_cascade: cascade
    })

    if (error) {
      return { data: null, error }
    }

    return {
      data: {
        annualGoal: data.annual_goal,
        quarterlyGoals: data.quarterly_goals || [],
        weeklyGoals: data.weekly_goals || []
      },
      error: null
    }
  },

  async updateGoalOrder(goalType, goals) {
    let table = ''
    switch (goalType) {
      case 'annual':
        table = 'annual_goals'
        break
      case 'quarterly':
        table = 'quarterly_goals'
        break
      case 'weekly':
        table = 'weekly_goals'
        break
      default:
        throw new Error('Invalid goal type')
    }

    // Update sort_order for all goals
    const updates = goals.map((goal, index) => ({
      id: goal.id,
      sort_order: index
    }))

    const { data, error } = await supabase
      .from(table)
      .upsert(updates, { onConflict: 'id' })
      .select('*')

    return { data: { goalType, data }, error }
  }
}

// Daily Check-Ins API functions
export const dailyCheckInsAPI = {
  // Get daily check-in for a specific date
  async getDailyCheckIn(userId, date) {
    const { data, error } = await supabase
      .from('daily_check_ins')
      .select('*')
      .eq('user_id', userId)
      .eq('check_in_date', date)
      .maybeSingle()  // Use maybeSingle() instead of single() - returns null if not found without error

    return { data, error }
  },

  // Save/update daily check-in (upsert)
  async saveDailyCheckIn(userId, checkInData) {
    const { data, error } = await supabase
      .from('daily_check_ins')
      .upsert({
        user_id: userId,
        check_in_date: checkInData.check_in_date,
        focus_goal: checkInData.focus_goal,
        big_3_task_ids: checkInData.big_3_task_ids || [],
        stretch_task_ids: checkInData.stretch_task_ids || [],
        day_rating: checkInData.day_rating || null,
        reflection: checkInData.reflection || null
      }, {
        onConflict: 'user_id,check_in_date'
      })
      .select()
      .single()

    return { data, error }
  },

  // Delete daily check-in
  async deleteDailyCheckIn(userId, date) {
    const { error } = await supabase
      .from('daily_check_ins')
      .delete()
      .eq('user_id', userId)
      .eq('check_in_date', date)

    return { error }
  },

  // Get check-ins for a date range (for calendar dots)
  async getCheckInsInRange(userId, startDate, endDate) {
    const { data, error } = await supabase
      .from('daily_check_ins')
      .select('check_in_date, big_3_task_ids, stretch_task_ids, focus_goal')
      .eq('user_id', userId)
      .gte('check_in_date', startDate)
      .lte('check_in_date', endDate)

    return { data, error }
  }
}

// Task Labels API functions
export const labelsAPI = {
  // Get all labels for a user
  async getAllLabels(userId) {
    const { data, error } = await supabase
      .from('task_labels')
      .select('*')
      .eq('user_id', userId)
      .order('name', { ascending: true })

    return { data, error }
  },

  // Create new label
  async createLabel(userId, labelData) {
    const { data, error } = await supabase
      .from('task_labels')
      .insert([{ ...labelData, user_id: userId }])
      .select()
      .single()

    return { data, error }
  },

  // Update label
  async updateLabel(labelId, updates) {
    const { data, error } = await supabase
      .from('task_labels')
      .update(updates)
      .eq('id', labelId)
      .select()
      .single()

    return { data, error }
  },

  // Delete label (cascade will remove assignments)
  async deleteLabel(labelId) {
    const { data, error } = await supabase
      .from('task_labels')
      .delete()
      .eq('id', labelId)
      .select()
      .single()

    return { data, error }
  },

  // Assign label to task
  async assignLabelToTask(taskId, labelId) {
    const { data, error } = await supabase
      .from('task_label_assignments')
      .insert([{ task_id: taskId, label_id: labelId }])
      .select()
      .single()

    return { data, error }
  },

  // Remove label from task
  async removeLabelFromTask(taskId, labelId) {
    const { data, error } = await supabase
      .from('task_label_assignments')
      .delete()
      .eq('task_id', taskId)
      .eq('label_id', labelId)

    return { data, error }
  },

  // Bulk update task labels (efficient upsert)
  async bulkUpdateTaskLabels(taskId, labelIds, userId) {
    try {
      // Validate userId is provided (required for RLS and Realtime)
      if (!userId) {
        return { data: null, error: new Error('userId is required for bulkUpdateTaskLabels') }
      }

      // First, get current label assignments for this task
      const { data: currentAssignments, error: fetchError } = await supabase
        .from('task_label_assignments')
        .select('label_id')
        .eq('task_id', taskId)

      if (fetchError) return { data: null, error: fetchError }

      const currentLabelIds = currentAssignments.map(a => a.label_id)

      // Determine which labels to add and which to remove
      const labelsToAdd = labelIds.filter(id => !currentLabelIds.includes(id))
      const labelsToRemove = currentLabelIds.filter(id => !labelIds.includes(id))

      // Remove labels no longer needed
      if (labelsToRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('task_label_assignments')
          .delete()
          .eq('task_id', taskId)
          .in('label_id', labelsToRemove)

        if (deleteError) return { data: null, error: deleteError }
      }

      // Add new labels (now includes user_id for RLS)
      if (labelsToAdd.length > 0) {
        const newAssignments = labelsToAdd.map(labelId => ({
          task_id: taskId,
          label_id: labelId,
          user_id: userId  // Required for RLS and Realtime event broadcasting
        }))

        const { error: insertError } = await supabase
          .from('task_label_assignments')
          .insert(newAssignments)

        if (insertError) return { data: null, error: insertError }
      }

      // Return success with the final label IDs
      return { data: labelIds, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }
}

// Loops API functions
export const loopsAPI = {
  // Get all loops for a user (both open and closed)
  async getAllLoops(userId) {
    const { data, error } = await supabase
      .from('loops')
      .select(`
        *,
        tasks (
          id,
          title,
          completed,
          status_id,
          task_statuses (
            id,
            name,
            color,
            is_completion_status
          )
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error && import.meta.env.DEV) {
      console.error('[loopsAPI] getAllLoops failed:', error)
    }

    return { data, error }
  },

  // Create new loop
  async createLoop(userId, loopData) {
    // Validate note length - max 1000 characters
    const MAX_NOTE_LENGTH = 1000
    if (loopData.note && loopData.note.length > MAX_NOTE_LENGTH) {
      return { data: null, error: { message: `Note cannot exceed ${MAX_NOTE_LENGTH} characters` } }
    }

    // Validate color input - default to yellow if invalid
    const ALLOWED_COLORS = [
      'yellow', 'pink', 'blue', 'green', 'orange', 'purple', 'teal', 'red',
      'cyan', 'lime', 'amber', 'indigo', 'rose', 'violet', 'fuchsia',
      'emerald', 'sky', 'slate', 'stone', 'gray'
    ]
    const validatedData = {
      ...loopData,
      color: ALLOWED_COLORS.includes(loopData.color) ? loopData.color : 'yellow'
    }

    const { data, error } = await supabase
      .from('loops')
      .insert([{ ...validatedData, user_id: userId }])
      .select(`
        *,
        tasks (
          id,
          title,
          completed,
          status_id,
          task_statuses (
            id,
            name,
            color,
            is_completion_status
          )
        )
      `)
      .single()

    if (error && import.meta.env.DEV) {
      console.error('[loopsAPI] createLoop failed:', error)
    }

    return { data, error }
  },

  // Update loop
  async updateLoop(loopId, updates) {
    // Validate note length if being updated - max 1000 characters
    const MAX_NOTE_LENGTH = 1000
    if (updates.note && updates.note.length > MAX_NOTE_LENGTH) {
      return { data: null, error: { message: `Note cannot exceed ${MAX_NOTE_LENGTH} characters` } }
    }

    // Auto-set closed_at when status changes to 'closed'
    const updatesWithTimestamp = { ...updates }
    if (updates.status === 'closed' && !updates.closed_at) {
      updatesWithTimestamp.closed_at = new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('loops')
      .update(updatesWithTimestamp)
      .eq('id', loopId)
      .select(`
        *,
        tasks (
          id,
          title,
          completed,
          status_id,
          task_statuses (
            id,
            name,
            color,
            is_completion_status
          )
        )
      `)
      .single()

    if (error && import.meta.env.DEV) {
      console.error('[loopsAPI] updateLoop failed:', error)
    }

    return { data, error }
  },

  // Close loop (set status to closed and closed_at timestamp)
  async closeLoop(loopId) {
    const { data, error } = await supabase
      .from('loops')
      .update({
        status: 'closed',
        closed_at: new Date().toISOString()
      })
      .eq('id', loopId)
      .select(`
        *,
        tasks (
          id,
          title,
          completed,
          status_id,
          task_statuses (
            id,
            name,
            color,
            is_completion_status
          )
        )
      `)
      .single()

    if (error && import.meta.env.DEV) {
      console.error('[loopsAPI] closeLoop failed:', error)
    }

    return { data, error }
  },

  // Reopen loop
  async reopenLoop(loopId) {
    const { data, error } = await supabase
      .from('loops')
      .update({
        status: 'open',
        closed_at: null
      })
      .eq('id', loopId)
      .select(`
        *,
        tasks (
          id,
          title,
          completed,
          status_id,
          task_statuses (
            id,
            name,
            color,
            is_completion_status
          )
        )
      `)
      .single()

    if (error && import.meta.env.DEV) {
      console.error('[loopsAPI] reopenLoop failed:', error)
    }

    return { data, error }
  },

  // Delete loop permanently
  async deleteLoop(loopId) {
    const { error } = await supabase
      .from('loops')
      .delete()
      .eq('id', loopId)

    if (error && import.meta.env.DEV) {
      console.error('[loopsAPI] deleteLoop failed:', error)
    }

    return { error }
  }
}

// Bootstrap API - Unified data loading
export const bootstrapAPI = {
  // Get all initial app data in a single optimized query
  async getBootstrapData(userId) {
    try {
      const { data, error } = await supabase
        .rpc('bootstrap_user_data', { user_uuid: userId })

      return { data, error }
    } catch (fetchError) {
      // Catch network errors that occur during the RPC call
      // Return as error object to maintain consistent API shape
      return { data: null, error: fetchError }
    }
  }
}

// Realtime API - Authentication and connection management
export const realtimeAPI = {
  /**
   * Set the access token for realtime connections
   * CRITICAL: Must be called before creating any realtime subscriptions
   * to ensure RLS policies can authenticate events
   */
  setAuth(accessToken) {
    if (!accessToken) {
      console.warn('[RealtimeAPI] setAuth called with no access token')
      return
    }

    console.log('[RealtimeAPI] Setting realtime access token')
    supabase.realtime.setAuth(accessToken)
  },

  /**
   * Get the current session and set realtime auth
   * Helper function that combines getting session + setting auth
   */
  async setAuthFromSession() {
    const { data: { session }, error } = await supabase.auth.getSession()

    if (error) {
      console.error('[RealtimeAPI] Error getting session for auth:', error)
      return { success: false, error }
    }

    if (!session?.access_token) {
      console.warn('[RealtimeAPI] No access token in session')
      return { success: false, error: new Error('No access token') }
    }

    this.setAuth(session.access_token)
    return { success: true, session }
  }
}

// Wiki API functions
export const wikiAPI = {
  // === SPACES ===

  // Get all wiki spaces for a user (includes cover_image_url and cover_gradient)
  async getAllSpaces(userId) {
    const { data, error } = await supabase
      .from('wiki_spaces')
      .select('*')
      .eq('user_id', userId)
      .eq('is_archived', false)
      .order('display_order', { ascending: true })

    return { data, error }
  },

  // Get archived wiki spaces for a user (for the Archived view)
  async getArchivedSpaces(userId) {
    const { data, error } = await supabase
      .from('wiki_spaces')
      .select('*')
      .eq('user_id', userId)
      .eq('is_archived', true)
      .order('archived_at', { ascending: false })

    return { data, error }
  },

  // Create new wiki space
  async createSpace(userId, spaceData) {
    const { data, error } = await supabase
      .from('wiki_spaces')
      .insert([{ ...spaceData, user_id: userId }])
      .select()
      .single()

    return { data, error }
  },

  // Update wiki space
  async updateSpace(spaceId, updates) {
    const { data, error } = await supabase
      .from('wiki_spaces')
      .update(updates)
      .eq('id', spaceId)
      .select()
      .single()

    return { data, error }
  },

  // Delete wiki space (cascade deletes pages)
  async deleteSpace(spaceId) {
    const { error } = await supabase
      .from('wiki_spaces')
      .delete()
      .eq('id', spaceId)

    return { error }
  },

  // Archive wiki space (cascade-archives its pages in one transaction)
  // Returns { space, page_ids } via the archive_wiki_space RPC
  async archiveSpace(spaceId, userId) {
    const { data, error } = await supabase.rpc('archive_wiki_space', {
      p_space_id: spaceId,
      p_user_id: userId
    })

    return { data, error }
  },

  // Restore an archived wiki space and its archived pages
  // Returns { space, pages } via the restore_wiki_space RPC
  async restoreSpace(spaceId, userId) {
    const { data, error } = await supabase.rpc('restore_wiki_space', {
      p_space_id: spaceId,
      p_user_id: userId
    })

    return { data, error }
  },

  // Update space display order (batch upsert for performance)
  async updateSpaceOrder(spaces) {
    // Prepare batch updates with only id and display_order
    const updates = spaces.map((space, index) => ({
      id: space.id,
      display_order: index,
      // Include required fields for upsert
      user_id: space.user_id,
      name: space.name
    }))

    const { data, error } = await supabase
      .from('wiki_spaces')
      .upsert(updates, { onConflict: 'id' })
      .select('id, display_order')

    return { data, error }
  },

  // === PAGES ===

  // Get all wiki pages metadata (no content, for tree display)
  async getAllPages(userId) {
    const { data, error } = await supabase
      .from('wiki_pages')
      .select('id, user_id, space_id, title, excerpt, parent_page_id, path, depth, display_order, is_pinned, is_favorite, is_archived, created_at, updated_at, last_viewed_at, scroll_position')
      .eq('user_id', userId)
      .eq('is_archived', false)
      .order('display_order', { ascending: true })

    return { data, error }
  },

  // Get archived wiki pages metadata for a user (for the Archived view)
  async getArchivedPages(userId) {
    const { data, error } = await supabase
      .from('wiki_pages')
      .select('id, user_id, space_id, title, slug, excerpt, parent_page_id, path, depth, display_order, is_pinned, is_favorite, is_archived, archived_at, created_at, updated_at, last_viewed_at, scroll_position, context_type, project_id, annual_goal_id, task_id')
      .eq('user_id', userId)
      .eq('is_archived', true)
      .order('archived_at', { ascending: false })

    return { data, error }
  },

  // Get full page content (on-demand when viewing/editing)
  async getPageWithContent(pageId) {
    const { data, error } = await supabase
      .from('wiki_pages')
      .select('*')
      .eq('id', pageId)
      .single()

    return { data, error }
  },

  // Create new wiki page
  async createPage(userId, pageData) {
    // Auto-generate excerpt from content
    const excerpt = pageData.content?.substring(0, 200) || null

    const { data, error } = await supabase
      .from('wiki_pages')
      .insert([{
        ...pageData,
        user_id: userId,
        excerpt
      }])
      .select()
      .single()

    return { data, error }
  },

  // Update wiki page
  async updatePage(pageId, updates) {
    const updateData = { ...updates }

    // Auto-update excerpt when content changes
    if (updates.content !== undefined) {
      updateData.excerpt = updates.content?.substring(0, 200) || null
    }

    const { data, error } = await supabase
      .from('wiki_pages')
      .update(updateData)
      .eq('id', pageId)
      .select()
      .single()

    return { data, error }
  },

  // Delete wiki page (cascade deletes children via trigger)
  async deletePage(pageId) {
    const { error } = await supabase
      .from('wiki_pages')
      .delete()
      .eq('id', pageId)

    return { error }
  },

  // Archive wiki page (cascade-archives its sub-tree in one transaction)
  // Returns { page_ids } via the archive_wiki_page RPC
  async archivePage(pageId, userId) {
    const { data, error } = await supabase.rpc('archive_wiki_page', {
      p_page_id: pageId,
      p_user_id: userId
    })

    return { data, error }
  },

  // Restore an archived wiki page and its archived sub-tree
  // Returns { pages } via the restore_wiki_page RPC
  async restorePage(pageId, userId) {
    const { data, error } = await supabase.rpc('restore_wiki_page', {
      p_page_id: pageId,
      p_user_id: userId
    })

    return { data, error }
  },

  // Move page to new parent or space
  async movePage(pageId, newParentId, newSpaceId = null) {
    const updates = { parent_page_id: newParentId }

    if (newSpaceId) {
      updates.space_id = newSpaceId
    }

    const { data, error } = await supabase
      .from('wiki_pages')
      .update(updates)
      .eq('id', pageId)
      .select()
      .single()

    return { data, error }
  },

  // Toggle favorite status
  async toggleFavorite(pageId, isFavorite) {
    const { data, error } = await supabase
      .from('wiki_pages')
      .update({ is_favorite: isFavorite })
      .eq('id', pageId)
      .select()
      .single()

    return { data, error }
  },

  // Toggle pinned status
  async togglePinned(pageId, isPinned) {
    const { data, error } = await supabase
      .from('wiki_pages')
      .update({ is_pinned: isPinned })
      .eq('id', pageId)
      .select()
      .single()

    return { data, error }
  },

  // Update page display order within parent (batch upsert for performance)
  async updatePageOrder(pages) {
    // Prepare batch updates with only id and display_order
    const updates = pages.map((page, index) => ({
      id: page.id,
      display_order: index,
      // Include required fields for upsert
      user_id: page.user_id,
      space_id: page.space_id,
      title: page.title
    }))

    const { data, error } = await supabase
      .from('wiki_pages')
      .upsert(updates, { onConflict: 'id' })
      .select('id, display_order')

    return { data, error }
  },

  // === SEARCH ===

  // Full-text search across wiki pages
  async searchPages(userId, query) {
    const { data, error } = await supabase
      .from('wiki_pages')
      .select('id, space_id, title, excerpt, is_favorite, is_pinned, updated_at')
      .eq('user_id', userId)
      .eq('is_archived', false)
      .textSearch('search_vector', query)
      .order('updated_at', { ascending: false })
      .limit(20)

    return { data, error }
  },

  // === ENTITY LINKS ===

  // Get all links from a page
  async getPageLinks(pageId) {
    const { data, error } = await supabase
      .from('wiki_entity_links')
      .select('*')
      .eq('page_id', pageId)

    return { data, error }
  },

  // Get backlinks to an entity (pages that link to this entity)
  // userId required for security - only return backlinks from user's own pages
  async getBacklinks(entityType, entityId, userId) {
    const { data, error } = await supabase
      .from('wiki_entity_links')
      .select(`
        *,
        wiki_pages!inner(id, title, space_id, is_archived, user_id)
      `)
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .eq('is_valid', true)
      .eq('wiki_pages.is_archived', false)
      .eq('wiki_pages.user_id', userId)

    return { data, error }
  },

  // Sync entity links for a page (delete old, insert new)
  async syncEntityLinks(pageId, userId, links) {
    // Delete existing links for this page
    const { error: deleteError } = await supabase
      .from('wiki_entity_links')
      .delete()
      .eq('page_id', pageId)

    if (deleteError) return { data: null, error: deleteError }

    // Insert new links if any
    if (links.length > 0) {
      const linksWithUser = links.map(link => ({
        ...link,
        page_id: pageId,
        user_id: userId
      }))

      const { data, error } = await supabase
        .from('wiki_entity_links')
        .insert(linksWithUser)
        .select()

      return { data, error }
    }

    return { data: [], error: null }
  },

  // === CONTINUE READING ===

  // Record that user viewed a page (updates last_viewed_at)
  async recordPageView(pageId) {
    const { data, error } = await supabase
      .from('wiki_pages')
      .update({
        last_viewed_at: new Date().toISOString()
      })
      .eq('id', pageId)
      .select('id, last_viewed_at')
      .single()

    return { data, error }
  },

  // Save scroll position when user leaves a page
  async saveScrollPosition(pageId, scrollPosition) {
    const { data, error } = await supabase
      .from('wiki_pages')
      .update({
        scroll_position: scrollPosition
      })
      .eq('id', pageId)
      .select('id, scroll_position')
      .single()

    return { data, error }
  },

  // Get the most recently viewed page for continue reading
  async getLastViewedPage(userId) {
    const { data, error } = await supabase
      .from('wiki_pages')
      .select('id, space_id, title, excerpt, last_viewed_at, scroll_position, updated_at')
      .eq('user_id', userId)
      .eq('is_archived', false)
      .not('last_viewed_at', 'is', null)
      .order('last_viewed_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    return { data, error }
  },

  // === SPACE COVERS ===

  // Update space cover (image URL or gradient)
  // Note: updated_at is handled automatically by database trigger
  async updateSpaceCover(spaceId, { coverImageUrl, coverGradient }) {
    const updates = {}

    // Clear both if setting one, or clear both if both null
    if (coverImageUrl !== undefined) {
      updates.cover_image_url = coverImageUrl
      updates.cover_gradient = null
    }
    if (coverGradient !== undefined) {
      updates.cover_gradient = coverGradient
      updates.cover_image_url = null
    }

    const { data, error } = await supabase
      .from('wiki_spaces')
      .update(updates)
      .eq('id', spaceId)
      .select()
      .single()

    return { data, error }
  },

  // Upload space cover image to storage
  async uploadSpaceCoverImage(userId, spaceId, file) {
    try {
      // Validate file size (5MB limit)
      const MAX_FILE_SIZE = 5 * 1024 * 1024
      if (file.size > MAX_FILE_SIZE) {
        return { data: null, error: { message: 'Image must be less than 5MB.' } }
      }

      // Validate MIME type
      const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        return { data: null, error: { message: 'Invalid file type. Only JPEG, PNG, GIF, and WebP images are supported.' } }
      }

      // Validate file extension - only allow image types
      const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp']
      const fileExt = file.name.split('.').pop()?.toLowerCase()
      if (!fileExt || !ALLOWED_EXTENSIONS.includes(fileExt)) {
        return { data: null, error: { message: 'Invalid file extension. Only images allowed (jpg, jpeg, png, gif, webp).' } }
      }

      // Generate unique file path
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `${userId}/${spaceId}/${fileName}`

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('wiki-space-covers')
        .upload(filePath, file)

      if (uploadError) {
        throw uploadError
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('wiki-space-covers')
        .getPublicUrl(filePath)

      return { data: { publicUrl: urlData?.publicUrl, storagePath: filePath }, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Delete space cover image from storage
  async deleteSpaceCoverImage(storagePath) {
    try {
      const { error } = await supabase.storage
        .from('wiki-space-covers')
        .remove([storagePath])

      if (error) {
        console.warn('Failed to delete space cover from storage:', error)
      }

      return { error: null }
    } catch (error) {
      return { error }
    }
  }
}
